import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gameAPI } from '../services/api';
import { formatNumber } from '../utils/formatters';
import { useStore } from '../store/useStore';
import { useSound } from '../hooks/useSound';
import { useAdsgram } from '../hooks/useAdsgram';
import { useTaddy } from '../hooks/useTaddy';
import './EarnPage.css';

// SVG иконки
import CheckCircleIcon from '../assets/icons/check-circle.svg';
import CoinsIcon from '../assets/icons/coins.svg';
import CalendarIcon from '../assets/icons/calendar.svg';
import ShareIcon from '../assets/icons/share.svg';
import TrophyIcon from '../assets/icons/trophy.svg';
import TargetIcon from '../assets/icons/target.svg';
import GamepadIcon from '../assets/icons/gamepad.svg';
import TelegramIcon from '../assets/icons/telegram.svg';
import UsersIcon from '../assets/icons/users.svg';
import DiamondIcon from '../assets/icons/diamond.svg';
import CrownIcon from '../assets/icons/crown.svg';
import StarIcon from '../assets/icons/star.svg';
import RocketIcon from '../assets/icons/rocket.svg';
import SwordIcon from '../assets/icons/sword.svg';
import ShieldIcon from '../assets/icons/shield.svg';
import GemIcon from '../assets/icons/gem.svg';
import FlagIcon from '../assets/icons/flag.svg';
import FireIcon from '../assets/icons/fire.svg';
import WalletIcon from '../assets/icons/wallet.svg';
import PlayCircleIcon from '../assets/icons/play-circle.svg';

interface Task {
  id: number;
  name: string;
  description: string;
  reward: number;
  status: 'pending' | 'in_progress' | 'completed' | 'claimed';
  category?: string;
  actionUrl?: string;
  action_url?: string;
  checkType?: string;
  check_type?: string;
  taskCode?: string;
  progress?: number;
  maxProgress?: number;
  max_progress?: number;
  completedAt?: string;
  completed_at?: string;
  startedAt?: string;
}

interface TasksData {
  daily: Task[];
  social: Task[];
  partners: Task[];
  achievements: Task[];
}

const EarnPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { graphicsQuality } = useStore();

  const playButtonClick = useSound('ui/ui_button_click.mp3');
  const [tasks, setTasks] = useState<TasksData>({
    daily: [],
    social: [],
    partners: [],
    achievements: []
  });
  const [activeCategory, setActiveCategory] = useState<'daily' | 'social' | 'achievements' | 'ads'>('daily');
  const [loading, setLoading] = useState(true);
  const [totalRewards, setTotalRewards] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [processingTask, setProcessingTask] = useState<number | null>(null);
  const [failedTask, setFailedTask] = useState<number | null>(null);
  const [timerTasks, setTimerTasks] = useState<Record<number, number>>({}); // taskId → секунды до конца
  const [adLoading, setAdLoading] = useState(false);
  const [adWatched, setAdWatched] = useState(0);
  const [showAdsNotice, setShowAdsNotice] = useState(false);
  const adsNoticeShownRef = useRef(false);
  const [adsEarned, setAdsEarned] = useState(0);
  const [adsTop, setAdsTop] = useState<Array<{rank: number, username: string, views: number, earned: number}>>([]);
  const [adCooldown, setAdCooldown] = useState(0);
  const [adLimited, setAdLimited] = useState(false);
  const [adLimitMessage, setAdLimitMessage] = useState('');
  const adCooldownRef = useRef<NodeJS.Timeout | null>(null);
  const [slideClass, setSlideClass] = useState<string>('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const hasLoadedRef = useRef(false);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoCheckingRef = useRef<Set<number>>(new Set()); // Таски которые уже в авто-проверке
  const loadRequestRef = useRef(0); // Счётчик запросов — защита от race condition

  // Ротация рекламных сетей: Adsgram ↔ Taddy (чередуем по очереди)
  const [adProvider, setAdProvider] = useState<'adsgram' | 'taddy'>(() => {
    const saved = localStorage.getItem('lastAdProvider');
    return saved === 'adsgram' ? 'taddy' : 'adsgram'; // следующий после последнего
  });

  // AdsGram + Taddy — просмотр рекламы за SPERM
  const adRewardRef = useRef<() => void>(() => {});
  const adErrorRef = useRef<(r: any) => void>(() => {});

  adRewardRef.current = () => {
    // Задержка 1 сек — даём серверу время обработать reward callback
    setTimeout(async () => {
      setAdLoading(false);
      const prevEarned = adsEarned;

      // Сначала проверяем статистику — начислилась ли награда
      try {
        const telegramId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
        if (telegramId) {
          const res = await gameAPI.getAdsMyStats(telegramId);
          if (res.data.ok) {
            const newEarned = res.data.totalEarned || 0;
            setAdsEarned(newEarned);
            setAdWatched(res.data.views || 0);

            // Проверяем лимиты
            if (res.data.limited) {
              setAdLimited(true);
              setAdLimitMessage(res.data.limitMessage || '');
            } else {
              setAdLimited(false);
              setAdLimitMessage('');
            }

            // Ставим таймер cooldown
            const waitSec = res.data.nextAvailableIn || 0;
            if (waitSec > 0) {
              setAdCooldown(waitSec);
              if (adCooldownRef.current) clearInterval(adCooldownRef.current);
              adCooldownRef.current = setInterval(() => {
                setAdCooldown(prev => {
                  if (prev <= 1) {
                    if (adCooldownRef.current) clearInterval(adCooldownRef.current);
                    return 0;
                  }
                  return prev - 1;
                });
              }, 1000);
            }

            // Награда начислена — показываем успех
            if (newEarned > prevEarned) {
              try { (window.Telegram?.WebApp as any)?.HapticFeedback?.impactOccurred('medium'); } catch(e) {}
              try {
                const audio = new Audio('/sounds/task_daily.mp3');
                audio.volume = 0.7;
                audio.play().catch(() => {});
              } catch(e) {}
              const n = document.createElement('div');
              n.className = 'earn-notification success';
              n.textContent = t('earn.ads.rewardNotification', { amount: '0.01' });
              n.style.cssText = 'position:fixed;top:50px;left:50%;transform:translateX(-50%);padding:12px 20px;border-radius:10px;color:white;font-weight:600;font-size:14px;z-index:9999;animation:slideDown 0.3s ease;max-width:90%;text-align:center;background:#4CAF50;';
              document.body.appendChild(n);
              setTimeout(() => n.remove(), 3000);
            } else if (res.data.limited) {
              // Лимит — показываем причину
              try { (window.Telegram?.WebApp as any)?.HapticFeedback?.notificationOccurred('warning'); } catch(e) {}
              const n = document.createElement('div');
              n.className = 'earn-notification info';
              n.textContent = res.data.limitMessage || 'Лимит просмотров исчерпан';
              n.style.cssText = 'position:fixed;top:50px;left:50%;transform:translateX(-50%);padding:12px 20px;border-radius:10px;color:white;font-weight:600;font-size:14px;z-index:9999;animation:slideDown 0.3s ease;max-width:90%;text-align:center;background:#FF9800;';
              document.body.appendChild(n);
              setTimeout(() => n.remove(), 3000);
            } else if (waitSec > 0) {
              // Кулдаун — показываем таймер
              try { (window.Telegram?.WebApp as any)?.HapticFeedback?.notificationOccurred('warning'); } catch(e) {}
              const n = document.createElement('div');
              n.className = 'earn-notification info';
              n.textContent = `Подождите ${waitSec} сек между просмотрами`;
              n.style.cssText = 'position:fixed;top:50px;left:50%;transform:translateX(-50%);padding:12px 20px;border-radius:10px;color:white;font-weight:600;font-size:14px;z-index:9999;animation:slideDown 0.3s ease;max-width:90%;text-align:center;background:#FF9800;';
              document.body.appendChild(n);
              setTimeout(() => n.remove(), 3000);
            }
            return;
          }
        }
      } catch(e) { /* stats недоступны — fallback */ }

      // Fallback если stats не загрузились — показываем успех как раньше
      try { (window.Telegram?.WebApp as any)?.HapticFeedback?.impactOccurred('medium'); } catch(e) {}
      const n = document.createElement('div');
      n.className = 'earn-notification success';
      n.textContent = t('earn.ads.rewardNotification', { amount: '0.01' });
      n.style.cssText = 'position:fixed;top:50px;left:50%;transform:translateX(-50%);padding:12px 20px;border-radius:10px;color:white;font-weight:600;font-size:14px;z-index:9999;animation:slideDown 0.3s ease;max-width:90%;text-align:center;background:#4CAF50;';
      document.body.appendChild(n);
      setTimeout(() => n.remove(), 3000);
    }, 1000);
  };

  adErrorRef.current = (result: any) => {
    setAdLoading(false);
    const isNotLoaded = result?.description === 'Adsgram script not loaded' || result?.description === 'Taddy script not loaded';
    const msg = isNotLoaded ? t('earn.ads.notLoaded') : t('earn.ads.unavailable');
    const n = document.createElement('div');
    n.className = 'earn-notification info';
    n.textContent = msg;
    n.style.cssText = 'position:fixed;top:50px;left:50%;transform:translateX(-50%);padding:12px 20px;border-radius:10px;color:white;font-weight:600;font-size:14px;z-index:9999;animation:slideDown 0.3s ease;max-width:90%;text-align:center;background:#7B3FF2;';
    document.body.appendChild(n);
    setTimeout(() => n.remove(), 3000);
  };

  const onAdReward = useCallback(() => adRewardRef.current(), []);
  const onAdError = useCallback((r: any) => adErrorRef.current(r), []);

  // Taddy: onReward получает adId — нужно отправить на бэкенд для начисления
  const onTaddyReward = useCallback((adId: string) => {
    const telegramId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
    if (telegramId) {
      gameAPI.sendTaddyReward(telegramId, adId).then(() => {
        adRewardRef.current();
      }).catch(() => {
        adRewardRef.current(); // всё равно пробуем обновить stats
      });
    }
  }, []);
  const onTaddyError = useCallback((msg: string) => {
    adErrorRef.current({ error: true, done: false, state: 'load', description: msg });
  }, []);

  const showAdsgram = useAdsgram({ blockId: '22774', onReward: onAdReward, onError: onAdError });
  const showTaddy = useTaddy({ pubId: '2820b9c6c7caaa88876b3ab4f3c18593', onReward: onTaddyReward, onError: onTaddyError });

  const handleWatchAd = () => {
    setAdLoading(true);
    try { (window.Telegram?.WebApp as any)?.HapticFeedback?.impactOccurred('light'); } catch(e) {}

    // Ротация: показываем из текущего провайдера, затем переключаем
    if (adProvider === 'taddy' && window.Taddy) {
      showTaddy();
      localStorage.setItem('lastAdProvider', 'taddy');
      setAdProvider('adsgram');
    } else {
      // Adsgram по дефолту (или если Taddy не загрузился)
      showAdsgram();
      localStorage.setItem('lastAdProvider', 'adsgram');
      setAdProvider('taddy');
    }
  };

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadTasks();
    }

    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (adCooldownRef.current) clearInterval(adCooldownRef.current);
    };
  }, []);

  // Тикаем таймеры каждую секунду
  useEffect(() => {
    if (Object.keys(timerTasks).length > 0) {
      timerIntervalRef.current = setInterval(() => {
        setTimerTasks(prev => {
          const updated: Record<number, number> = {};
          let hasActive = false;
          for (const [id, seconds] of Object.entries(prev)) {
            const remaining = seconds - 1;
            if (remaining > 0) {
              updated[Number(id)] = remaining;
              hasActive = true;
            } else {
              updated[Number(id)] = 0;
            }
          }
          return updated;
        });
      }, 1000);
    } else if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [Object.keys(timerTasks).length > 0]);

  // Автопроверка каналов когда таймер истёк
  useEffect(() => {
    const expiredIds = Object.entries(timerTasks)
      .filter(([, sec]) => sec === 0)
      .map(([id]) => Number(id));

    for (const taskId of expiredIds) {
      if (autoCheckingRef.current.has(taskId)) continue; // Уже проверяем
      autoCheckingRef.current.add(taskId);

      // Найдём задачу чтобы определить checkType
      const allTasks = [...tasks.daily, ...tasks.social, ...tasks.partners, ...tasks.achievements];
      const task = allTasks.find(t => t.id === taskId);
      const checkType = task?.checkType || task?.check_type;

      // Автопроверка только для telegram_channel (timer — юзер нажимает вручную)
      if (checkType === 'telegram_channel') {
        setProcessingTask(taskId);
        (async () => {
          try {
            const isPartner = taskId >= 100000;
            const checkResult = isPartner ? await checkPartnerTask(taskId) : await checkTask(taskId);
            handleTaskResult(taskId, checkResult);
          } catch (e) { console.error('Autocheck error:', e); }
          setProcessingTask(null);
          setTimerTasks(prev => {
            const copy = { ...prev };
            delete copy[taskId];
            return copy;
          });
          autoCheckingRef.current.delete(taskId);
          await loadTasks(true);
        })();
      }
    }
  }, [timerTasks]);

  const loadTasks = async (silent = false) => {
    const requestId = ++loadRequestRef.current;
    try {
      if (!silent) setLoading(true);

      const token = localStorage.getItem('token');
      const isTelegram = window.Telegram?.WebApp?.initData;
      const isWebAuth = localStorage.getItem('webauth') === 'true';

      if (!token && isTelegram && !isWebAuth) {
        try {
          const initData = window.Telegram?.WebApp?.initData || '';
          const telegramId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;

          if (telegramId) {
            const authResponse = await gameAPI.authTelegram(initData, telegramId);
            if (authResponse.data.success && authResponse.data.token) {
              localStorage.setItem('token', authResponse.data.token);
            }
          }
        } catch (authError) {
          console.error('Authorization failed:', authError);
        }
      }

      let response;

      try {
        response = await gameAPI.getTasks();
      } catch (error) {
        try {
          response = await gameAPI.getPublicTasks();
        } catch (publicError) {
          // В silent режиме: если API недоступен — оставляем текущие данные
          if (silent) return;
          response = {
            data: {
              success: true,
              tasks: { daily: [], social: [], achievements: [] }
            }
          };
        }
      }

      if (response.data.success) {
        const tasksData = response.data.tasks || response.data.data || {};

        const formattedTasks: TasksData = {
          daily: tasksData.daily || [],
          social: tasksData.social || [],
          partners: [],
          achievements: tasksData.achievements || []
        };

        // Загружаем партнёрские каналы (пропускаем в веб-версии — всё равно фильтруются)
        const isWebMode = localStorage.getItem('webauth') === 'true';
        if (!isWebMode) {
          try {
            const partnersRes = await gameAPI.getPartnerChannels();
            if (partnersRes.data.success && partnersRes.data.data) {
              formattedTasks.partners = partnersRes.data.data
                .filter((ch: any) => ch.channel_name)
                .map((ch: any) => {
                  let status: 'pending' | 'completed' | 'claimed' = 'pending';
                  if (ch.user_status === 'completed') status = 'completed';
                  else if (ch.user_status === 'pending_reward') status = 'completed'; // Ожидает 24ч — показываем как выполненное
                  return {
                    id: ch.id + 100000,
                    name: ch.channel_name,
                    description: ch.description || '',
                    reward: parseFloat(ch.reward_per_subscription) || 0.1,
                    status,
                    category: 'partner',
                    actionUrl: ch.channel_url,
                    checkType: 'telegram_channel',
                  };
                });
            }
          } catch (e) { /* партнёры недоступны */ }
        }

        // Защита от race condition: если пришёл ответ от старого запроса — игнорируем
        if (requestId !== loadRequestRef.current) return;

        // Фильтр по языку: RU юзеры видят tg_chat_join, EN юзеры видят tg_chat_join_en
        const lang = i18n.language || 'ru';
        const hideCodes = lang === 'en'
          ? ['tg_chat_join']       // EN юзеры не видят RU чат
          : ['tg_chat_join_en'];   // RU юзеры не видят EN чат
        formattedTasks.social = formattedTasks.social.filter(
          (task: Task) => !hideCodes.includes(task.taskCode || '')
        );

        // Веб-версия: скрываем задания с подпиской на каналы (нельзя проверить)
        if (localStorage.getItem('webauth') === 'true') {
          formattedTasks.social = formattedTasks.social.filter(
            (task: Task) => task.checkType !== 'telegram_channel'
          );
          formattedTasks.partners = [];
        }

        // Защита от обнуления: в silent-режиме не перетираем данные пустым ответом
        const totalTasksCount = formattedTasks.daily.length + formattedTasks.social.length + formattedTasks.achievements.length;
        if (silent && totalTasksCount === 0) return;

        setTasks(formattedTasks);

        // Статистика: задания (без рекламы)
        let rewards = 0;
        let completed = 0;

        ['daily', 'social', 'partners', 'achievements'].forEach((cat) => {
          const categoryTasks = (formattedTasks as any)[cat] || [];
          if (Array.isArray(categoryTasks)) {
            categoryTasks.forEach((task: Task) => {
              if (task.status === 'completed' || task.status === 'claimed') {
                rewards += task.reward;
                completed++;
              }
            });
          }
        });

        // Загружаем статистику и топ рекламы (пропускаем в веб — реклама недоступна)
        if (!isWebMode) {
          try {
            const telegramId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
            if (telegramId) {
              const adsRes = await gameAPI.getAdsMyStats(telegramId);
              if (adsRes.data.ok) {
                setAdsEarned(adsRes.data.totalEarned || 0);
                setAdWatched(adsRes.data.views || 0);
                rewards += adsRes.data.totalEarned || 0;
                if (adsRes.data.limited) {
                  setAdLimited(true);
                  setAdLimitMessage(adsRes.data.limitMessage || '');
                } else {
                  setAdLimited(false);
                  setAdLimitMessage('');
                }
                const waitSec = adsRes.data.nextAvailableIn || 0;
                if (waitSec > 0) {
                  setAdCooldown(waitSec);
                  if (adCooldownRef.current) clearInterval(adCooldownRef.current);
                  adCooldownRef.current = setInterval(() => {
                    setAdCooldown(prev => {
                      if (prev <= 1) {
                        if (adCooldownRef.current) clearInterval(adCooldownRef.current);
                        return 0;
                      }
                      return prev - 1;
                    });
                  }, 1000);
                }
              }
            }
          } catch (e) { /* ads stats недоступны */ }

          try {
            const topRes = await gameAPI.getAdsTop();
            if (topRes.data.ok && topRes.data.top) {
              setAdsTop(topRes.data.top);
            }
          } catch (e) { /* ads top недоступен */ }
        }

        setTotalRewards(rewards);
        setCompletedCount(completed);
      }
    } catch (error) {
      console.error('Ошибка загрузки заданий:', error);
    } finally {
      if (requestId === loadRequestRef.current && !silent) setLoading(false);
    }
  };

  const triggerHaptic = (style: string = 'medium') => {
    try {
      (window.Telegram?.WebApp as any)?.HapticFeedback?.impactOccurred(style);
    } catch (e) { /* игнорируем */ }
  };

  const playResultSound = (success: boolean) => {
    if (success) {
      playTaskSound(activeCategory);
    } else {
      playRejectSound();
    }
  };

  const handleTaskResult = (taskId: number, result: CheckResult) => {
    triggerHaptic(result.success ? 'medium' : 'light');
    playResultSound(result.success);
    showNotification(result.message, result.type);
    if (!result.success) {
      setFailedTask(taskId);
      setTimeout(() => setFailedTask(null), 2000);
    }
  };

  const handleTaskClick = async (task: Task) => {
    if (task.status === 'completed' || task.status === 'claimed') {
      return;
    }

    // Если таймер ещё тикает — ждём
    if (timerTasks[task.id] && timerTasks[task.id] > 0) {
      return;
    }

    // Вибрация при нажатии (START)
    triggerHaptic('light');

    // Минимальная задержка анимации (2 сек)
    const minDelay = () => new Promise(resolve => setTimeout(resolve, 2000));

    // Если таймер истёк (= 0) — можно проверить
    if (timerTasks[task.id] === 0) {
      setProcessingTask(task.id);
      try {
        const isPartner = task.id >= 100000;
        const [checkResult] = await Promise.all([
          isPartner ? checkPartnerTask(task.id) : checkTask(task.id),
          minDelay()
        ]);
        handleTaskResult(task.id, checkResult);
      } finally {
        setProcessingTask(null);
        await loadTasks(true);
        setTimerTasks(prev => {
          const copy = { ...prev };
          delete copy[task.id];
          return copy;
        });
      }
      return;
    }

    setProcessingTask(task.id);

    try {
      const actionUrl = task.actionUrl || task.action_url;
      const checkType = task.checkType || task.check_type;

      if (actionUrl) {
        // Открываем ссылку без ухода из приложения
        try {
          if (actionUrl.includes('t.me/') && window.Telegram?.WebApp?.openTelegramLink) {
            window.Telegram.WebApp.openTelegramLink(actionUrl);
          } else if (window.Telegram?.WebApp?.openLink) {
            window.Telegram.WebApp.openLink(actionUrl);
          } else {
            window.open(actionUrl, '_blank');
          }
        } catch (e) {
          window.open(actionUrl, '_blank');
        }

        // Отмечаем задание как начатое на сервере (только для обычных задач)
        if (task.status === 'pending' && task.id < 100000) {
          try { await gameAPI.startTask(task.id); } catch(e) {}
        }

        // Для timer-тасков запускаем обратный отсчёт 30 секунд
        if (checkType === 'timer') {
          setTimerTasks(prev => ({ ...prev, [task.id]: 30 }));
          setProcessingTask(null);
          return;
        }

        // Для telegram_channel — видимый таймер 5 сек, потом автопроверка
        // В веб-версии увеличиваем таймер (нет способа проверить подписку)
        if (checkType === 'telegram_channel') {
          const isWeb = localStorage.getItem('webauth') === 'true';
          setTimerTasks(prev => ({ ...prev, [task.id]: isWeb ? 10 : 5 }));
          setProcessingTask(null);
          return;
        }

        // Для auto с actionUrl — таймер 30 сек, потом кнопка "Проверить"
        if (checkType === 'auto') {
          setTimerTasks(prev => ({ ...prev, [task.id]: 30 }));
          setProcessingTask(null);
          return;
        }

        // Для остальных — проверяем сразу
        setProcessingTask(null);
        return;
      }

      // Для auto и manual — проверяем с минимальной анимацией
      const isPartner = task.id >= 100000;
      const [checkResult] = await Promise.all([
        isPartner ? checkPartnerTask(task.id) : checkTask(task.id),
        minDelay()
      ]);
      handleTaskResult(task.id, checkResult);
    } catch (error) {
      console.error('Ошибка выполнения задания:', error);
    } finally {
      if (!(task.checkType === 'timer' || task.check_type === 'timer')) {
        setProcessingTask(null);
        await loadTasks(true);
      }
    }
  };

  const playTaskSound = (category: string) => {
    try {
      const soundMap: Record<string, string> = {
        daily: '/sounds/task_daily.mp3',
        social: '/sounds/task_social.mp3',
        achievements: '/sounds/task_achievement.mp3',
      };
      const src = soundMap[category] || soundMap.daily;
      const audio = new Audio(src);
      audio.volume = 0.7;
      audio.play().catch(() => {});
    } catch (e) { /* игнорируем */ }
  };

  const playRejectSound = () => {
    try {
      const audio = new Audio('/sounds/task_reject.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch (e) { /* игнорируем */ }
  };

  interface CheckResult {
    success: boolean;
    message: string;
    type: 'success' | 'error' | 'info';
  }

  // Возвращает результат БЕЗ показа уведомления — уведомление покажет handleTaskClick после анимации
  const checkTask = async (taskId: number): Promise<CheckResult> => {
    try {
      const response = await gameAPI.checkTask(taskId);

      if (response.data.success && response.data.data) {
        const data = response.data.data;

        if (data.completed) {
          if (data.stakingRequired) {
            return { success: false, message: data.message || `Нужен стейк от ${data.minStaking} SPERM`, type: 'info' };
          } else if (data.pending) {
            return { success: true, message: t('earn.rewardPending'), type: 'info' };
          } else if (data.alreadyDone) {
            return { success: true, message: data.message || 'Задание уже выполнено', type: 'info' };
          } else if (data.reward > 0) {
            return { success: true, message: `+${data.reward} SPERM!`, type: 'success' };
          } else {
            return { success: true, message: data.message || 'Задание выполнено!', type: 'success' };
          }
        } else {
          if (data.secondsLeft && data.secondsLeft > 0) {
            setTimerTasks(prev => ({ ...prev, [taskId]: data.secondsLeft }));
          }
          return { success: false, message: data.message || 'Условия не выполнены', type: 'error' };
        }
      }
      return { success: false, message: 'Ошибка проверки', type: 'error' };
    } catch (error: any) {
      console.error('Error checking task:', error);
      if (error?.response?.status === 429) {
        return { success: false, message: 'Подождите несколько секунд', type: 'error' };
      }
      return { success: false, message: 'Ошибка проверки задания', type: 'error' };
    }
  };

  // Проверка подписки на партнёрский канал (отдельный эндпоинт)
  const checkPartnerTask = async (taskId: number): Promise<CheckResult> => {
    try {
      const channelId = taskId - 100000; // Восстанавливаем реальный ID канала
      const response = await gameAPI.checkPartnerTask(channelId);

      if (response.data.success && response.data.data) {
        const data = response.data.data;

        if (data.completed) {
          if (data.pending) {
            return { success: true, message: t('earn.rewardPending'), type: 'info' };
          } else if (data.alreadyDone) {
            return { success: true, message: data.message || 'Задание уже выполнено', type: 'info' };
          } else if (data.reward > 0) {
            return { success: true, message: `+${data.reward} SPERM!`, type: 'success' };
          }
          return { success: true, message: data.message || 'Задание выполнено!', type: 'success' };
        } else {
          return { success: false, message: data.message || 'Вы не подписаны на канал', type: 'error' };
        }
      }
      return { success: false, message: 'Ошибка проверки', type: 'error' };
    } catch (error) {
      console.error('Error checking partner task:', error);
      return { success: false, message: 'Ошибка проверки подписки', type: 'error' };
    }
  };

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    const notification = document.createElement('div');
    notification.className = `earn-notification ${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 50px;
      left: 50%;
      transform: translateX(-50%);
      padding: 12px 20px;
      border-radius: 10px;
      color: white;
      font-weight: 600;
      font-size: 14px;
      z-index: 9999;
      animation: slideDown 0.3s ease;
      max-width: 90%;
      text-align: center;
      background: ${type === 'success' ? '#4CAF50' : type === 'error' ? '#f44336' : '#FF9800'};
    `;

    document.body.appendChild(notification);

    // info (жёлтая плашка) — дольше показываем
    const duration = type === 'info' ? 5000 : 3000;
    setTimeout(() => {
      notification.remove();
    }, duration);
  };

  const getTaskIcon = (task: Task): string => {
    const code = task.taskCode || '';

    // По task_code — надёжнее чем по имени
    const iconMap: Record<string, string> = {
      daily_login: TargetIcon,
      daily_claim: CoinsIcon,
      daily_game: GamepadIcon,
      claim_3_times_48h: CoinsIcon,
      tg_channel_subscribe: TelegramIcon,
      tg_chat_join: TelegramIcon,
      twitter_follow: ShareIcon,
      youtube_subscribe: ShareIcon,
      share_referral: ShareIcon,
      invite_1_friend: UsersIcon,
      invite_5_friends: CrownIcon,
      first_deposit: WalletIcon,
      deposit_100: WalletIcon,
      active_staking: RocketIcon,
      streak_7_days: FireIcon,
      rank_uchanik: ShieldIcon,
      rank_boec: ShieldIcon,
      rank_voin: ShieldIcon,
      rank_kapitan: ShieldIcon,
      rank_champion: ShieldIcon,
      rank_paladin: ShieldIcon,
      vip_bronze: GemIcon,
      vip_silver: GemIcon,
      vip_gold: GemIcon,
      vip_platinum: GemIcon,
      vip_diamond: GemIcon,
      leader_bronze: FlagIcon,
      leader_silver: FlagIcon,
      leader_gold: FlagIcon,
      leader_platinum: FlagIcon,
      slots_first_spin: GamepadIcon,
      slots_50_spins: GamepadIcon,
      slots_500_spins: GamepadIcon,
      slots_5000_spins: FireIcon,
      slots_first_win: StarIcon,
      slots_big_win: DiamondIcon,
      slots_jackpot: CrownIcon,
      slots_bonus_5: RocketIcon,
      slots_win_streak_5: FireIcon,
      slots_total_won_1000: TrophyIcon,
      slots_100_spins: GamepadIcon,
      invite_10_friends: UsersIcon,
      invite_30_friends: UsersIcon,
      invite_50_friends: UsersIcon,
      invite_100_friends: CrownIcon,
      streak_14_days: FireIcon,
      streak_30_days: FireIcon,
      streak_60_days: FireIcon,
      stake_500: GemIcon,
      stake_2000: GemIcon,
      stake_8000: DiamondIcon,
      connect_wallet: WalletIcon,
      first_swap: CoinsIcon,
      jarvis_first_use: RocketIcon,
    };

    if (iconMap[code]) return iconMap[code];

    // Fallback по категории
    if (task.category === 'daily') return CalendarIcon;
    if (task.category === 'social') return ShareIcon;
    if (task.category === 'achievements') return TrophyIcon;
    return TargetIcon;
  };

  // Локализованное имя задания (i18n по task_code, fallback на БД)
  const getTaskName = (task: Task): string => {
    const code = task.taskCode;
    if (code) {
      const key = `earn.taskNames.${code}`;
      const translated = t(key);
      if (translated !== key) return translated;
    }
    return task.name;
  };

  const getTaskDesc = (task: Task): string => {
    const code = task.taskCode;
    if (code) {
      const key = `earn.taskNames.${code}_desc`;
      const translated = t(key);
      if (translated !== key) return translated;
    }
    return task.description;
  };

  // --- Аккордеон-группы для Achievements с прогрессивным показом ---
  const achievementGroupDefs: { key: string; label: string; codes: string[]; chains: string[][] }[] = [
    {
      key: 'referrals', label: t('earn.groups.referrals'),
      codes: ['share_referral', 'invite_1_friend', 'invite_5_friends', 'invite_10_friends', 'invite_30_friends', 'invite_50_friends', 'invite_100_friends'],
      chains: [['invite_1_friend', 'invite_5_friends', 'invite_10_friends', 'invite_30_friends', 'invite_50_friends', 'invite_100_friends']]
    },
    {
      key: 'streak', label: t('earn.groups.streak'),
      codes: ['streak_7_days', 'streak_14_days', 'streak_30_days', 'streak_60_days'],
      chains: [['streak_7_days', 'streak_14_days', 'streak_30_days', 'streak_60_days']]
    },
    {
      key: 'staking', label: t('earn.groups.staking'),
      codes: ['first_deposit', 'deposit_100', 'active_staking', 'stake_500', 'stake_2000', 'stake_8000', 'connect_wallet', 'first_swap'],
      chains: [['active_staking', 'stake_500', 'stake_2000', 'stake_8000'], ['first_deposit', 'deposit_100']]
    },
    {
      key: 'vip', label: t('earn.groups.vip'),
      codes: ['vip_bronze', 'vip_silver', 'vip_gold', 'vip_platinum', 'vip_diamond'],
      chains: [['vip_bronze', 'vip_silver', 'vip_gold', 'vip_platinum', 'vip_diamond']]
    },
    {
      key: 'leaders', label: t('earn.groups.leaders'),
      codes: ['leader_bronze', 'leader_silver', 'leader_gold', 'leader_platinum'],
      chains: [['leader_bronze', 'leader_silver', 'leader_gold', 'leader_platinum']]
    },
    {
      key: 'slots', label: t('earn.groups.slots'),
      codes: ['slots_first_spin', 'slots_50_spins', 'slots_100_spins', 'slots_500_spins', 'slots_5000_spins', 'slots_first_win', 'slots_big_win', 'slots_jackpot', 'slots_jackpot_5', 'slots_bonus_5', 'slots_bonus_win_250x', 'slots_bonus_win_300x', 'slots_win_streak_5', 'slots_total_won_1000'],
      chains: [
        ['slots_first_spin', 'slots_50_spins', 'slots_100_spins', 'slots_500_spins', 'slots_5000_spins'],
        ['slots_jackpot', 'slots_jackpot_5'],
        ['slots_bonus_win_250x', 'slots_bonus_win_300x']
      ]
    },
    {
      key: 'other', label: t('earn.groups.other'),
      codes: ['jarvis_first_use'],
      chains: []
    },
  ];

  // Получить видимые таски для группы (с прогрессивным показом)
  const getVisibleGroupTasks = (group: typeof achievementGroupDefs[0]) => {
    const allAch = tasks.achievements || [];
    // Таски этой группы
    const groupTasks = allAch.filter((t: Task) => group.codes.includes(t.taskCode || ''));
    if (groupTasks.length === 0) return [];

    // Определяем какие таски из цепочек скрыть
    const hiddenCodes = new Set<string>();
    for (const chain of group.chains) {
      const chainTasks = chain.map(code => groupTasks.find((t: Task) => t.taskCode === code)).filter(Boolean) as Task[];
      if (chainTasks.length <= 1) continue;

      // Найти первый невыполненный в цепочке
      const firstIncompleteIdx = chainTasks.findIndex(t => t.status !== 'completed' && t.status !== 'claimed');

      for (let i = 0; i < chainTasks.length; i++) {
        const isCompleted = chainTasks[i].status === 'completed' || chainTasks[i].status === 'claimed';
        if (firstIncompleteIdx === -1) {
          // Все выполнены — показать только последний
          if (i < chainTasks.length - 1) hiddenCodes.add(chainTasks[i].taskCode || '');
        } else if (i < firstIncompleteIdx - 1) {
          // Скрыть все выполненные кроме предпоследнего перед текущим
          hiddenCodes.add(chainTasks[i].taskCode || '');
        } else if (i > firstIncompleteIdx) {
          // Скрыть все будущие после текущего невыполненного
          hiddenCodes.add(chainTasks[i].taskCode || '');
        }
      }
    }

    return groupTasks.filter((t: Task) => !hiddenCodes.has(t.taskCode || ''));
  };

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Группы с хотя бы одним таском
  const achievementGroups = achievementGroupDefs
    .map(g => ({ ...g, tasks: getVisibleGroupTasks(g), allTasks: (tasks.achievements || []).filter((t: Task) => g.codes.includes(t.taskCode || '')) }))
    .filter(g => g.allTasks.length > 0);

  // Таски без группы (на всякий)
  const groupedCodes = new Set(achievementGroupDefs.flatMap(g => g.codes));
  const ungroupedTasks = (tasks.achievements || []).filter((t: Task) => !groupedCodes.has(t.taskCode || ''));

  const getTabStats = (category: 'daily' | 'social' | 'achievements' | 'partners') => {
    const categoryTasks = tasks[category] || [];
    const completed = categoryTasks.filter((t: Task) => t.status === 'completed' || t.status === 'claimed').length;
    const total = categoryTasks.length;
    return { completed, total };
  };

  const getChannelsStats = () => {
    const s = getTabStats('social');
    const p = getTabStats('partners');
    return { completed: s.completed + p.completed, total: s.total + p.total };
  };

  const getCurrentTasks = () => {
    if (activeCategory === 'social') return tasks.social;
    return (tasks as any)[activeCategory] || [];
  };

  const getTaskButtonText = (task: Task) => {
    const checkType = task.checkType || task.check_type;

    if (task.status === 'completed' || task.status === 'claimed') return null;

    // Таймер тикает
    if (timerTasks[task.id] !== undefined && timerTasks[task.id] > 0) {
      return `${timerTasks[task.id]} ${t('earn.sec')}`;
    }

    // Таймер истёк — можно забрать
    if (timerTasks[task.id] === 0) {
      return t('earn.verify');
    }

    if (checkType === 'timer' || checkType === 'telegram_channel') {
      return t('earn.goTo');
    }

    return t('earn.start');
  };

  const categoryOrder = ['daily', 'social', 'achievements', 'ads'];

  const switchCategory = (newCategory: 'daily' | 'social' | 'achievements' | 'ads') => {
    if (newCategory === activeCategory) return;
    playButtonClick();

    // Показать уведомление при первом переходе на вкладку ADS за сессию
    if (newCategory === 'ads' && !adsNoticeShownRef.current) {
      adsNoticeShownRef.current = true;
      setShowAdsNotice(true);
    }

    // При низкой графике — мгновенное переключение
    if (graphicsQuality === 'low') {
      setActiveCategory(newCategory);
      return;
    }

    const oldIdx = categoryOrder.indexOf(activeCategory);
    const newIdx = categoryOrder.indexOf(newCategory);
    const dir = newIdx > oldIdx ? 'left' : 'right';
    // Фаза 1: уезжает старый контент
    setSlideClass(`slide-out-${dir}`);
    setTimeout(() => {
      // Фаза 2: меняем вкладку, въезжает новый контент
      setActiveCategory(newCategory);
      setSlideClass(`slide-in-${dir}`);
      // Фаза 3: убираем класс анимации чтобы loadTasks не триггерил повтор
      setTimeout(() => setSlideClass(''), 250);
    }, 150);
  };

  if (loading) {
    return (
      <div className="earn-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('earn.loading')}</p>
        </div>
      </div>
    );
  }


  return (
    <div className={`earn-page ${graphicsQuality === 'low' ? 'low-graphics' : ''}`}>
      <div className="page-container">
        {/* Статистика — 3 карточки */}
        <div className="earn-stats three-cols">
          <div className="stat-card">
            <span className="stat-icon"><img src={CheckCircleIcon} alt="" /></span>
            <div className="stat-info">
              <span className="stat-value">{completedCount}</span>
              <span className="stat-label">{t('earn.completed')}</span>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon"><img src={DiamondIcon} alt="" /></span>
            <div className="stat-info">
              <span className="stat-value">{formatNumber(totalRewards, 2)}</span>
              <span className="stat-label">{t('earn.earned')}</span>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon"><img src={PlayCircleIcon} alt="" /></span>
            <div className="stat-info">
              <span className="stat-value">{formatNumber(adsEarned, 2)}</span>
              <span className="stat-label">{t('earn.adsEarned')}</span>
            </div>
          </div>
        </div>

        {/* Категории */}
        <div className="task-categories">
          <button
            className={`category-btn ${activeCategory === 'daily' ? 'active' : ''}`}
            onClick={() => switchCategory('daily')}
          >
            <span className="category-icon"><img src={CalendarIcon} alt="" /></span>
            <span className="category-name">{t('earn.daily')}</span>
            <span className="category-badge">
              {getTabStats('daily').completed}/{getTabStats('daily').total}
            </span>
          </button>
          <button
            className={`category-btn ${activeCategory === 'social' ? 'active' : ''}`}
            onClick={() => switchCategory('social')}
          >
            <span className="category-icon"><img src={ShareIcon} alt="" /></span>
            <span className="category-name">{t('earn.social')}</span>
            <span className="category-badge">
              {getChannelsStats().completed}/{getChannelsStats().total}
            </span>
          </button>
          <button
            className={`category-btn ${activeCategory === 'achievements' ? 'active' : ''}`}
            onClick={() => switchCategory('achievements')}
          >
            <span className="category-icon"><img src={TrophyIcon} alt="" /></span>
            <span className="category-name">{t('earn.achievements')}</span>
            <span className="category-badge">
              {getTabStats('achievements').completed}/{getTabStats('achievements').total}
            </span>
          </button>
          <button
            className={`category-btn ${activeCategory === 'ads' ? 'active' : ''}`}
            onClick={() => switchCategory('ads')}
          >
            <span className="category-icon"><img src={PlayCircleIcon} alt="" /></span>
            <span className="category-name">Ads</span>
            <span className="category-badge free-badge">FREE</span>
          </button>
        </div>

        {/* Список заданий / Реклама */}
        <div className={`tasks-list ${slideClass}`}>
          {activeCategory === 'ads' ? (
            localStorage.getItem('webauth') === 'true' ? (
            /* Веб-версия: заглушка — реклама недоступна */
            <div className="ads-section">
              <div className="ads-web-unavailable">
                <div className="ads-web-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ffd700" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                    <line x1="2" y1="3" x2="22" y2="17" stroke="#ff4444" strokeWidth="2"/>
                  </svg>
                </div>
                <h3 className="ads-web-title">{t('earn.ads.webUnavailable')}</h3>
                <p className="ads-web-desc">{t('earn.ads.webUnavailableDesc')}</p>
                <a
                  href="https://t.me/moonraid_game_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ads-web-tg-btn"
                >
                  {t('earn.ads.openInTelegram')}
                </a>
              </div>
            </div>
            ) : (
            /* Вкладка рекламы — Telegram */
            <div className="ads-section">
              <div className="ad-card-big" onClick={(adLoading || adCooldown > 0 || adLimited) ? undefined : handleWatchAd}>
                <div className="ad-card-top">
                  <div className="ad-icon-big"><img src={PlayCircleIcon} alt="" /></div>
                  <div className="ad-content-big">
                    <h3 className="ad-title-big">{t('earn.ads.title')}</h3>
                    <p className="ad-description-big">{t('earn.ads.description')}</p>
                  </div>
                </div>
                <div className="ad-card-bottom">
                  <div className="ad-reward-pill">
                    <span className="reward-text">+0.01 SPERM</span>
                  </div>
                  {adLimited ? (
                    <div className="ad-limit-message">
                      <span>{adLimitMessage}</span>
                    </div>
                  ) : adCooldown > 0 ? (
                    <button className="ad-watch-btn cooldown" disabled>
                      {Math.floor(adCooldown / 60)}:{String(adCooldown % 60).padStart(2, '0')}
                    </button>
                  ) : (
                    <button className={`ad-watch-btn ${adLoading ? 'loading' : ''}`} disabled={adLoading}>
                      {adLoading ? '⏳' : t('earn.ads.watch')}
                    </button>
                  )}
                </div>
              </div>
              {adWatched > 0 && (
                <div className="ad-stats">
                  <span className="ad-stats-text">{t('earn.ads.viewed', { count: adWatched })} | {t('earn.ads.earnedAmount', { amount: formatNumber(adsEarned, 2) })}</span>
                </div>
              )}
              <p className="ad-click-hint">
                {t('earn.ads.clickHint_before')}
                <span className="ad-click-highlight">{t('earn.ads.clickHint_highlight')}</span>
                {t('earn.ads.clickHint_after')}
              </p>
              <p className="ad-anti-abuse">{t('earn.ads.antiAbuse_line1')}</p>
              <p className="ad-anti-abuse">{t('earn.ads.antiAbuse_line2')}</p>
              <div className="ad-network-notice">
                <p>{t('earn.ads.networkNotice', { defaultValue: 'Между просмотрами действует кулдаун (30–250 сек). Дневной лимит — 50 просмотров. При превышении лимита награда не начисляется.' })}</p>
              </div>

              {/* Топ 15 по просмотрам рекламы */}
              <div className="ads-top-section">
                <h4 className="ads-top-title">{t('earn.ads.topTitle')}</h4>
                {adsTop.length > 0 ? (
                  <div className="ads-top-list">
                    {adsTop.map((item) => (
                      <div key={item.rank} className={`ads-top-item ${item.rank <= 3 ? 'top-three' : ''}`}>
                        <span className={`ads-top-rank rank-${item.rank}`}>
                          {item.rank <= 3 ? ['🥇', '🥈', '🥉'][item.rank - 1] : `#${item.rank}`}
                        </span>
                        <span className="ads-top-name">{item.username}</span>
                        <span className="ads-top-views">{item.views} {t('earn.ads.topViews')}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="ads-top-empty">{t('earn.ads.topEmpty')}</p>
                )}
              </div>
            </div>
            )
          ) : activeCategory === 'social' ? (
            /* Вкладка Каналы — 2 секции */
            <div className="channels-sections">
              <div className="section-header">{t('earn.ourChannels')}</div>
              {tasks.social.length === 0 ? (
                <div className="no-tasks"><p>{t('earn.noTasks')}</p></div>
              ) : (
                tasks.social.map((task: Task) => {
                  const buttonText = getTaskButtonText(task);
                  const isTimerActive = timerTasks[task.id] !== undefined && timerTasks[task.id] > 0;
                  const isTimerReady = timerTasks[task.id] === 0;
                  return (
                    <div key={task.id} className={`task-card ${task.status} ${processingTask === task.id ? 'processing' : ''} ${isTimerActive ? 'timer-active' : ''} ${isTimerReady ? 'timer-ready' : ''}`} onClick={() => handleTaskClick(task)}>
                      <div className="task-icon"><img src={getTaskIcon(task)} alt="" /></div>
                      <div className="task-content">
                        <h3 className="task-title">{getTaskName(task)}</h3>
                        <p className="task-description">{getTaskDesc(task)}</p>
                      </div>
                      <div className="task-right">
                        <div className="task-reward"><span className="reward-text">+{task.reward} SPERM</span></div>
                        {failedTask === task.id ? (
                          <span className="status-failed"></span>
                        ) : task.status === 'completed' || task.status === 'claimed' ? (
                          <span className="status-completed"></span>
                        ) : buttonText ? (
                          <button className={`task-action-btn ${isTimerActive ? 'disabled' : ''} ${isTimerReady ? 'ready' : ''}`} disabled={isTimerActive || processingTask === task.id}>
                            {processingTask === task.id ? '⏳' : buttonText}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}

              {tasks.partners.length > 0 && (
                <>
                  <div className="section-header">{t('earn.partners')}</div>
                  {tasks.partners.map((task: Task) => {
                    const buttonText = getTaskButtonText(task);
                    const isTimerActive = timerTasks[task.id] !== undefined && timerTasks[task.id] > 0;
                    const isTimerReady = timerTasks[task.id] === 0;
                    return (
                      <div key={task.id} className={`task-card ${task.status} ${processingTask === task.id ? 'processing' : ''} ${isTimerActive ? 'timer-active' : ''} ${isTimerReady ? 'timer-ready' : ''}`} onClick={() => handleTaskClick(task)}>
                        <div className="task-icon"><img src={ShareIcon} alt="" /></div>
                        <div className="task-content">
                          <h3 className="task-title">{getTaskName(task)}</h3>
                          <p className="task-description">{getTaskDesc(task)}</p>
                        </div>
                        <div className="task-right">
                          <div className="task-reward"><span className="reward-text">+{task.reward} SPERM</span></div>
                          {failedTask === task.id ? (
                            <span className="status-failed"></span>
                          ) : task.status === 'completed' || task.status === 'claimed' ? (
                            <span className="status-completed"></span>
                          ) : buttonText ? (
                            <button className={`task-action-btn ${isTimerActive ? 'disabled' : ''} ${isTimerReady ? 'ready' : ''}`} disabled={isTimerActive || processingTask === task.id}>
                              {processingTask === task.id ? '⏳' : buttonText}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          ) : activeCategory === 'achievements' ? (
            /* Achievements — аккордеон-группы с прогрессивным показом */
            <div className="achievement-groups">
              {achievementGroups.map(group => {
                const completedCount = group.allTasks.filter((t: Task) => t.status === 'completed' || t.status === 'claimed').length;
                const isExpanded = expandedGroups[group.key] !== false; // По умолчанию развёрнуты
                return (
                  <div key={group.key} className={`achievement-group ${isExpanded ? 'expanded' : 'collapsed'}`}>
                    <div className="group-header" onClick={() => toggleGroup(group.key)}>
                      <span className="group-title">{group.label}</span>
                      <span className="group-stats">
                        <span className="group-counter">{completedCount}/{group.allTasks.length}</span>
                        <span className={`group-arrow ${isExpanded ? 'up' : 'down'}`}>▼</span>
                      </span>
                    </div>
                    {isExpanded && (
                      <div className="group-tasks">
                        {group.tasks.map((task: Task) => {
                          const buttonText = getTaskButtonText(task);
                          return (
                            <div key={task.id} className={`task-card ${task.status} ${processingTask === task.id ? 'processing' : ''}`} onClick={() => handleTaskClick(task)}>
                              <div className="task-icon"><img src={getTaskIcon(task)} alt="" /></div>
                              <div className="task-content">
                                <h3 className="task-title">{getTaskName(task)}</h3>
                                <p className="task-description">{getTaskDesc(task)}</p>
                                {task.progress !== undefined && task.progress > 0 && task.maxProgress && task.maxProgress > 1 && (
                                  <div className="task-progress">
                                    <div className="progress-bar">
                                      <div className="progress-fill" style={{ width: `${(task.progress / task.maxProgress) * 100}%` }} />
                                    </div>
                                    <span className="progress-text">{task.progress} / {task.maxProgress}</span>
                                  </div>
                                )}
                              </div>
                              <div className="task-right">
                                <div className="task-reward"><span className="reward-text">+{task.reward} SPERM</span></div>
                                {failedTask === task.id ? (
                                  <span className="status-failed"></span>
                                ) : task.status === 'completed' || task.status === 'claimed' ? (
                                  <span className="status-completed"></span>
                                ) : buttonText ? (
                                  <button className="task-action-btn" disabled={processingTask === task.id}>
                                    {processingTask === task.id ? '⏳' : buttonText}
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {ungroupedTasks.length > 0 && ungroupedTasks.map((task: Task) => {
                const buttonText = getTaskButtonText(task);
                return (
                  <div key={task.id} className={`task-card ${task.status}`} onClick={() => handleTaskClick(task)}>
                    <div className="task-icon"><img src={getTaskIcon(task)} alt="" /></div>
                    <div className="task-content">
                      <h3 className="task-title">{getTaskName(task)}</h3>
                      <p className="task-description">{getTaskDesc(task)}</p>
                    </div>
                    <div className="task-right">
                      <div className="task-reward"><span className="reward-text">+{task.reward} SPERM</span></div>
                      {task.status === 'completed' || task.status === 'claimed' ? (
                        <span className="status-completed"></span>
                      ) : buttonText ? (
                        <button className="task-action-btn">{buttonText}</button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : getCurrentTasks().length === 0 ? (
            <div className="no-tasks">
              <span className="no-tasks-icon">📭</span>
              <p>{t('earn.noTasks')}</p>
            </div>
          ) : (
            getCurrentTasks().map((task: Task) => {
              const buttonText = getTaskButtonText(task);
              const isTimerActive = timerTasks[task.id] !== undefined && timerTasks[task.id] > 0;
              const isTimerReady = timerTasks[task.id] === 0;
              return (
                <div key={task.id} className={`task-card ${task.status} ${processingTask === task.id ? 'processing' : ''} ${isTimerActive ? 'timer-active' : ''} ${isTimerReady ? 'timer-ready' : ''}`} onClick={() => handleTaskClick(task)}>
                  <div className="task-icon"><img src={getTaskIcon(task)} alt="" /></div>
                  <div className="task-content">
                    <h3 className="task-title">{getTaskName(task)}</h3>
                    <p className="task-description">{getTaskDesc(task)}</p>
                    {task.progress !== undefined && task.progress > 0 && task.maxProgress && task.maxProgress > 1 && (
                      <div className="task-progress">
                        <div className="progress-bar">
                          <div className="progress-fill" style={{ width: `${(task.progress / task.maxProgress) * 100}%` }} />
                        </div>
                        <span className="progress-text">{task.progress} / {task.maxProgress}</span>
                      </div>
                    )}
                  </div>
                  <div className="task-right">
                    <div className="task-reward"><span className="reward-text">+{task.reward} SPERM</span></div>
                    {failedTask === task.id ? (
                      <span className="status-failed"></span>
                    ) : task.status === 'completed' || task.status === 'claimed' ? (
                      <span className="status-completed"></span>
                    ) : buttonText ? (
                      <button className={`task-action-btn ${isTimerActive ? 'disabled' : ''} ${isTimerReady ? 'ready' : ''}`} disabled={isTimerActive || processingTask === task.id}>
                        {processingTask === task.id ? '⏳' : buttonText}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          from { transform: translateX(-50%) translateY(-20px); opacity: 0; }
          to { transform: translateX(-50%) translateY(0); opacity: 1; }
        }
      `}</style>

      {/* Модалка-уведомление при переходе на ADS */}
      {showAdsNotice && (
        <div className="ads-notice-overlay" onClick={() => setShowAdsNotice(false)}>
          <div className="ads-notice-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ads-notice-icon">⚠️</div>
            <div className="ads-notice-block">
              <div className="ads-notice-title">{t('earn.ads.noticeClickTitle')}</div>
              <div className="ads-notice-text">{t('earn.ads.noticeClickText')}</div>
            </div>
            <div className="ads-notice-block">
              <div className="ads-notice-title">🛡️ {t('earn.ads.noticeAntifraudTitle')}</div>
              <div className="ads-notice-text">{t('earn.ads.noticeAntifraudText')}</div>
            </div>
            <button className="ads-notice-btn" onClick={() => setShowAdsNotice(false)}>
              {t('earn.ads.noticeGotIt')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EarnPage;
